/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

export enum EnvironmentTypes {
  DEVELOPMENT = 'DEVELOPMENT',
  STAGING = 'STAGING',
  PRODUCTION = 'PRODUCTION',
  PRE_PRODUCTION = 'PRE_PRODUCTION',
}

type EnvironmentURLMap = Record<EnvironmentTypes, string>;
interface EnvironmentURLsType {
  AUTH: EnvironmentURLMap;
  LFC: EnvironmentURLMap;
  STATIC: EnvironmentURLMap;
  STATION_SEARCH: EnvironmentURLMap;
  NEAREST_AIRPORTS: EnvironmentURLMap;
  FLIGHT_RECOMMENDATIONS: EnvironmentURLMap;
  HOTEL_RECOMMENDATIONS: EnvironmentURLMap;
  RECOMMENDATIONS_V3: EnvironmentURLMap;
  DUTY_FREE_RECOMMENDATIONS: EnvironmentURLMap;
  RECENT_SEARCHES_DEST_IMAGE: EnvironmentURLMap;
  FLIGHTS_PLACEHOLDER: EnvironmentURLMap;
  FUNNEL_CONTROL: EnvironmentURLMap;
  SEARCH_AGGREGATOR: EnvironmentURLMap;
  UPSELL_BUNDLE: EnvironmentURLMap;
  GUEST_AGGREGATOR: EnvironmentURLMap;
  GUEST_AGGREGATOR_MOT_VALIDATION: EnvironmentURLMap;
  MMB_GUEST_AGGREGATOR: EnvironmentURLMap;
  MMB_CHANGE_FLIGHT_GUEST_AGGREGATOR: EnvironmentURLMap;
  CHANGE_FLIGHT: EnvironmentURLMap;
  XSELL_AGGREGATOR: EnvironmentURLMap;
  STATE_PROVINCE: EnvironmentURLMap;
  BP_CALCULATOR: EnvironmentURLMap;
  SNAP_REDIRECTION: EnvironmentURLMap;
  SEARCH_AGGREGATOR_DEEPLINK_SERVICE: EnvironmentURLMap;
  CURRENCY_FORMAT: EnvironmentURLMap;
  INITIATE_CHECKOUT: EnvironmentURLMap;
  CHECKOUT_V4: EnvironmentURLMap;
  MOVETIX_CART_CHECKOUT: EnvironmentURLMap;
  FLIGHTS_ORIGIN: EnvironmentURLMap;
  DOWNLOAD_ITINERARY: EnvironmentURLMap;
  MANAGE_BOOKING: EnvironmentURLMap;
  CHECKOUT_API_MMB: EnvironmentURLMap;
  CHECKOUT_API_MMB_CHANGE_FLIGHT: EnvironmentURLMap;
  PURCHASES: EnvironmentURLMap;
  MALAYSIAN_EINVOICE: EnvironmentURLMap;
  HOMEPAGE: EnvironmentURLMap;
  OTA_CHECKIN: EnvironmentURLMap;
}

export const OTA_CHECKIN_PATH = '/fp/ota/pb';

export const EnvironmentURLs: EnvironmentURLsType = {
  AUTH: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://flights.devairasia.com/',
    [EnvironmentTypes.STAGING]: 'https://flights.stgairasia.com/',
    [EnvironmentTypes.PRODUCTION]: 'https://flights.airasia.com/',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://flights.ppairasia.com/',
  },

  STATIC: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://static.stgairasia.com/',
    [EnvironmentTypes.STAGING]: 'https://static.stgairasia.com/',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://static.airasia.com/',
    [EnvironmentTypes.PRODUCTION]: 'https://static.airasia.com/',
  },

  LFC: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.devairasia.com/fp/lfc/v1/lowfare',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/lfc/v1/lowfare', // staging fallback to dev
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/lfc/v1/lowfare',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/lfc/v1/lowfare',
  },

  STATION_SEARCH: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.devairasia.com/stations/v1/airports',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/stations/v1/airports', // staging fallback to dev
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/stations/v1/airports',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/stations/v1/airports',
  },

  NEAREST_AIRPORTS: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.devairasia.com/stations/v1/airports/nearest',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/stations/v1/airports/nearest',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/stations/v1/airports/nearest',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/stations/v1/airports/nearest',
  },

  FLIGHTS_PLACEHOLDER: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://static.stgairasia.com/design/images/flight/aircraft.png',
    [EnvironmentTypes.STAGING]:
      'https://static.stgairasia.com/design/images/flight/aircraft.png',
    [EnvironmentTypes.PRODUCTION]:
      'https://static.stgairasia.com/design/images/flight/aircraft.png',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://static.stgairasia.com/design/images/flight/aircraft.png',
  },

  FLIGHT_RECOMMENDATIONS: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://recommend.stg-apiairasia.com/v3/recommendations/',
    [EnvironmentTypes.STAGING]:
      'https://recommend.stg-apiairasia.com/v3/recommendations/',
    [EnvironmentTypes.PRODUCTION]:
      'https://recommend.apiairasia.com/v3/recommendations/',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://recommend.apiairasia.com/v3/recommendations/',
  },

  HOTEL_RECOMMENDATIONS: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://hotelsbe.stg-apiairasia.com/reco/v1/hotel/reco/widgets',
    [EnvironmentTypes.STAGING]:
      'https://hotelsbe.stg-apiairasia.com/reco/v1/hotel/reco/widgets',
    [EnvironmentTypes.PRODUCTION]:
      'https://hotelsbe.apiairasia.com/reco/v1/hotel/reco/widgets',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://hotelsbe.apiairasia.com/reco/v1/hotel/reco/widgets',
  },

  RECOMMENDATIONS_V3: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://recommend.stg-apiairasia.com/v3/recommendations/',
    [EnvironmentTypes.STAGING]:
      'https://recommend.stg-apiairasia.com/v3/recommendations/',
    [EnvironmentTypes.PRODUCTION]:
      'https://recommend.apiairasia.com/v3/recommendations/',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://recommend.apiairasia.com/v3/recommendations/',
  },

  DUTY_FREE_RECOMMENDATIONS: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://www.stgairasia.com/shop/api/inventory/v1/cross-sell/recommendations',
    [EnvironmentTypes.STAGING]:
      'https://www.stgairasia.com/shop/api/inventory/v1/cross-sell/recommendations',
    [EnvironmentTypes.PRODUCTION]:
      'https://www.airasia.com/shop/api/inventory/v1/cross-sell/recommendations',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://www.airasia.com/shop/api/inventory/v1/cross-sell/recommendations',
  },

  FUNNEL_CONTROL: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.devairasia.com/fp/funnels/v1/redirectionurls',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/funnels/v1/redirectionurls',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/funnels/v1/redirectionurls',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/funnels/v1/redirectionurls',
  },

  SEARCH_AGGREGATOR: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.devairasia.com/{platform}/fp/search/flights/v5/aggregated-results',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/{platform}/fp/search/flights/v5/aggregated-results',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/{platform}/fp/search/flights/v5/aggregated-results',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/{platform}/fp/search/flights/v5/aggregated-results',
  },

  UPSELL_BUNDLE: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.devairasia.com/fp/guest/aggregator/v1/upsell',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/guest/aggregator/v1/upsell',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/guest/aggregator/v1/upsell',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/guest/aggregator/v1/upsell',
  },

  GUEST_AGGREGATOR: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.stgairasia.com/fp/guest/aggregator/v4/page-results',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/guest/aggregator/v4/page-results',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/guest/aggregator/v4/page-results',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/guest/aggregator/v4/page-results',
  },
  GUEST_AGGREGATOR_MOT_VALIDATION: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.stgairasia.com/fp/guest/aggregator/v1/mot/flight-subsidies',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/guest/aggregator/v1/mot/flight-subsidies',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/guest/aggregator/v1/mot/flight-subsidies',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/guest/aggregator/v1/mot/flight-subsidies',
  },
  MMB_GUEST_AGGREGATOR: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.stgairasia.com/fp/guest/aggregator/v1/postbooking-results',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/guest/aggregator/v1/postbooking-results',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/guest/aggregator/v1/postbooking-results',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/guest/aggregator/v1/postbooking-results',
  },
  MMB_CHANGE_FLIGHT_GUEST_AGGREGATOR: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.devairasia.com/fp/guest/aggregator/v1/change-flight/page-results',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/guest/aggregator/v1/change-flight/page-results',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/guest/aggregator/v1/change-flight/page-results',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/guest/aggregator/v1/change-flight/page-results',
  },
  CHANGE_FLIGHT: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.devairasia.com/mmb/changeflight/api/v2',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/mmb/changeflight/api/v2',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/mmb/changeflight/api/v2',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/mmb/changeflight/api/v2',
  },
  XSELL_AGGREGATOR: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://flights-mmb.devairasia.com',
    [EnvironmentTypes.STAGING]: 'https://flights-mmb.stgairasia.com',
    [EnvironmentTypes.PRODUCTION]: 'https://flights-mmb.airasia.com',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://flights-mmb.airasia.com',
  },

  STATE_PROVINCE: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://ppsch.apiairasia.com',
    [EnvironmentTypes.STAGING]: 'https://ppsch.apiairasia.com',
    [EnvironmentTypes.PRODUCTION]: 'https://sch.apiairasia.com',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://ppsch.apiairasia.com',
  },

  RECENT_SEARCHES_DEST_IMAGE: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://static.airasia.com/design/images/destination/ds-{0}-med.jpg',
    [EnvironmentTypes.STAGING]:
      'https://static.airasia.com/design/images/destination/ds-{0}-med.jpg',
    [EnvironmentTypes.PRODUCTION]:
      'https://static.airasia.com/design/images/destination/ds-{0}-med.jpg',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://static.airasia.com/design/images/destination/ds-{0}-med.jpg',
  },

  BP_CALCULATOR: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://oms.stgairasia.com/bpCalculator/api/v1/bigpoint/calculation',
    [EnvironmentTypes.STAGING]:
      'https://oms.stgairasia.com/bpCalculator/api/v1/bigpoint/calculation',
    [EnvironmentTypes.PRODUCTION]:
      'https://oms.airasia.com/bpCalculator/api/v1/bigpoint/calculation',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://oms.airasia.com/bpCalculator/api/v1/bigpoint/calculation',
  },

  SNAP_REDIRECTION: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://www.devairasia.com/snap/hotels/list',
    [EnvironmentTypes.STAGING]: 'https://www.stgairasia.com/snap/hotels/list',
    [EnvironmentTypes.PRODUCTION]: 'https://www.airasia.com/snap/hotels/list',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://www.airasia.com/snap/hotels/list',
  },

  SEARCH_AGGREGATOR_DEEPLINK_SERVICE: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.devairasia.com/fp/deeplink/v1/links',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/deeplink/v1/links',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/deeplink/v1/links',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/deeplink/v1/links',
  },

  CURRENCY_FORMAT: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://static.stgairasia.com/cosmos/common/dataformat/currency',
    [EnvironmentTypes.STAGING]:
      'https://static.stgairasia.com/cosmos/common/dataformat/currency',
    [EnvironmentTypes.PRODUCTION]:
      'https://static.airasia.com/cosmos/common/dataformat/currency',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://static.airasia.com/cosmos/common/dataformat/currency',
  },

  INITIATE_CHECKOUT: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.stgairasia.com/fp/checkout/v3/initiatecheckout',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/checkout/v3/initiatecheckout',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/checkout/v3/initiatecheckout',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/checkout/v3/initiatecheckout',
  },

  CHECKOUT_V4: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.stgairasia.com/fp/checkout/v4/checkouts',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/checkout/v4/checkouts',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/checkout/v4/checkouts',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/checkout/v4/checkouts',
  },

  MOVETIX_CART_CHECKOUT: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.stgairasia.com/fp/checkout/v1/carts/',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/checkout/v1/carts/',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/checkout/v1/carts/',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/checkout/v1/carts/',
  },

  FLIGHTS_ORIGIN: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://www.devairasia.com',
    [EnvironmentTypes.STAGING]: 'https://www.stgairasia.com',
    [EnvironmentTypes.PRODUCTION]: 'https://www.airasia.com',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://www.airasia.com',
  },

  DOWNLOAD_ITINERARY: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights-mmb.devairasia.com/fp/itinerary-service/v3/itinerary/',
    [EnvironmentTypes.STAGING]:
      'https://flights-mmb.stgairasia.com/fp/itinerary-service/v3/itinerary/',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights-mmb.airasia.com/fp/itinerary-service/v3/itinerary/',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights-mmb.airasia.com/fp/itinerary-service/v3/itinerary/',
  },

  MANAGE_BOOKING: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights-mmb.devairasia.com/fp/manage-booking/v1/checkout',
    [EnvironmentTypes.STAGING]:
      'https://flights-mmb.stgairasia.com/fp/manage-booking/v1/checkout',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights-mmb.airasia.com/fp/manage-booking/v1/checkout',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights-mmb.airasia.com/fp/manage-booking/v1/checkout',
  },

  CHECKOUT_API_MMB: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.devairasia.com/fp/checkout/v1/checkout',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/checkout/v1/checkout',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/checkout/v1/checkout',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/checkout/v1/checkout',
  },

  CHECKOUT_API_MMB_CHANGE_FLIGHT: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://flights.devairasia.com/fp/checkout/v1/change-flight/checkout',
    [EnvironmentTypes.STAGING]:
      'https://flights.stgairasia.com/fp/checkout/v1/change-flight/checkout',
    [EnvironmentTypes.PRODUCTION]:
      'https://flights.airasia.com/fp/checkout/v1/change-flight/checkout',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://flights.airasia.com/fp/checkout/v1/change-flight/checkout',
  },

  PURCHASES: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://web.devairasia.com/purchases/',
    [EnvironmentTypes.STAGING]: 'https://web.stgairasia.com/purchases/',
    [EnvironmentTypes.PRODUCTION]: 'https://www.airasia.com/purchases/',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://www.airasia.com/purchases/',
  },

  MALAYSIAN_EINVOICE: {
    [EnvironmentTypes.DEVELOPMENT]:
      'https://oms.stgairasia.com/einvoice/api/v1/longform/redirection',
    [EnvironmentTypes.STAGING]:
      'https://oms.stgairasia.com/einvoice/api/v1/longform/redirection',
    [EnvironmentTypes.PRODUCTION]:
      'https://oms.airasia.com/einvoice/api/v1/longform/redirection',
    [EnvironmentTypes.PRE_PRODUCTION]:
      'https://oms.airasia.com/einvoice/api/v1/longform/redirection',
  },

  HOMEPAGE: {
    [EnvironmentTypes.DEVELOPMENT]: 'https://www.devairasia.com/en/gb',
    [EnvironmentTypes.STAGING]: 'https://www.stgairasia.com/en/gb',
    [EnvironmentTypes.PRODUCTION]: 'https://www.airasia.com/en/gb',
    [EnvironmentTypes.PRE_PRODUCTION]: 'https://www.ppairasia.com/en/gb',
  },

  OTA_CHECKIN: {
    [EnvironmentTypes.DEVELOPMENT]: `https://flights.devairasia.com${OTA_CHECKIN_PATH}`,
    [EnvironmentTypes.STAGING]: `https://flights.stgairasia.com${OTA_CHECKIN_PATH}`,
    [EnvironmentTypes.PRODUCTION]: `https://flights.airasia.com${OTA_CHECKIN_PATH}`,
    [EnvironmentTypes.PRE_PRODUCTION]: `https://flights.ppairasia.com${OTA_CHECKIN_PATH}`,
  },
};

export const parseEnv = (
  env: string | EnvironmentTypes = EnvironmentTypes.PRODUCTION,
): EnvironmentTypes => {
  const value = env.toString().toLowerCase();

  if (value.includes('dev')) {
    return EnvironmentTypes.DEVELOPMENT;
  }
  if (value.includes('staging') || value.includes('stg')) {
    return EnvironmentTypes.STAGING;
  }
  if (value.includes('prod') || value.includes('prd')) {
    return EnvironmentTypes.PRODUCTION;
  }
  if (value.includes('pp')) {
    return EnvironmentTypes.PRE_PRODUCTION;
  }

  return EnvironmentTypes.PRODUCTION;
};

export default EnvironmentURLs;
